import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';

export default function AdminPerformance() {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { getPerformanceMetrics } = await import('@/lib/monitoring');
        const data = await getPerformanceMetrics();
        setMetrics(data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="container mx-auto p-8">Loading...</div>;

  return (
    <>
      <Helmet>
        <title>Performance Metrics - Admin</title>
      </Helmet>
      <div className="container mx-auto p-8">
        <h1 className="text-3xl font-bold mb-8">Performance Metrics</h1>

        <div className="grid grid-cols-3 gap-6 mb-8">
          {metrics?.averages &&
            Object.entries(metrics.averages).map(([operation, avgMs]: any) => (
              <Card key={operation} className="p-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  {operation}
                </h3>
                <p className="text-3xl font-bold">
                  {avgMs.toFixed(0)}
                  <span className="text-lg text-muted-foreground">ms</span>
                </p>
                <p
                  className={`text-sm mt-2 ${
                    avgMs < 2000
                      ? 'text-green-500'
                      : avgMs < 5000
                      ? 'text-yellow-500'
                      : 'text-red-500'
                  }`}
                >
                  {avgMs < 2000 ? '✓ Fast' : avgMs < 5000 ? '⚠ Acceptable' : '✗ Slow'}
                </p>
              </Card>
            ))}
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Operations</h2>
          <div className="space-y-2">
            {metrics?.recent?.slice(0, 10).map((metric: any, i: number) => (
              <div
                key={i}
                className="flex items-center justify-between p-2 rounded hover:bg-accent"
              >
                <span className="font-medium">{metric.operation}</span>
                <div className="flex items-center gap-4">
                  {metric.cached && (
                    <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">
                      CACHED
                    </span>
                  )}
                  {metric.model && (
                    <span className="text-xs text-muted-foreground">
                      {metric.model}
                    </span>
                  )}
                  <span className="font-mono">{metric.duration}ms</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {metrics?.slowOperations && metrics.slowOperations.length > 0 && (
          <Card className="p-6 mt-8">
            <h2 className="text-xl font-semibold mb-4">Slow Operations (&gt;5s)</h2>
            <div className="space-y-2">
              {metrics.slowOperations.map((metric: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-2 rounded bg-red-500/10"
                >
                  <span className="font-medium">{metric.operation}</span>
                  <span className="font-mono text-red-500">{metric.duration}ms</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
